import { DATA_SOURCES } from '@/config/constants';
import DefinitionTooltip from '../../DefinitionTooltip';
import { InstagramIcon, TikTokIcon } from '../../Icons';
import TopPhrasesSection from '../../TopKeywordsSection/TopPhrasesSection';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import { getFlagData } from 'components/Keywords';
import { tooltips } from '@/copy';
import {
    useInstagramRelatedDrawerHashtags,
    useInstagramRelatedDrawerKeywords,
    useTikTokRelatedDrawerHashtags,
    useTikTokRelatedDrawerKeywords,
} from 'utils/hooks/api';

const hashtagIdOf = (row) => row.hashtagId;

const BY_SOURCE = {
    [DATA_SOURCES.TIKTOK]: {
        Icon: TikTokIcon,
        useKeywords: useTikTokRelatedDrawerKeywords,
        useHashtags: useTikTokRelatedDrawerHashtags,
        tooltip: tooltips.relatedTerms,
        tooltipContentKey: 'relatedTerms',
        sectionContentKey: 'TopRelatedTerms',
        volumeLabel: 'views',
        volumeOf: (row) => row.views,
    },
    [DATA_SOURCES.INSTAGRAM]: {
        Icon: InstagramIcon,
        useKeywords: useInstagramRelatedDrawerKeywords,
        useHashtags: useInstagramRelatedDrawerHashtags,
        tooltip: tooltips.instagramRelatedTerms,
        tooltipContentKey: 'instagramRelatedTerms',
        sectionContentKey: 'InstagramTopRelatedTerms',
        volumeLabel: 'likes',
        volumeOf: (row) => row.likes,
        hashtagIdOf: (row) => row.id,
    },
};

const Wrapper = (props) => <div {...props} />;

function TermsHeading({ children, showIcon, data }) {
    const spec = BY_SOURCE[data.dataSource];
    return (
        <div className="my-6 flex flex-wrap text-base font-semibold">
            <span css="display: flex; align-items: center; gap: .5rem;">
                {showIcon ? <spec.Icon /> : null}
                Keywords &amp; Hashtags
                <DefinitionTooltip
                    {...spec.tooltip}
                    week={data.week}
                    year={data.year}
                    mixpanelMeta={{ contentKey: spec.tooltipContentKey }}
                    openOnClick={true}
                />
            </span>
            {children}
        </div>
    );
}

function NoTerms() {
    return (
        <div
            css={`
                color: ${({ theme }) => theme.neutral_3};
            `}
        >
            No keywords or hashtags to show.
        </div>
    );
}

function RelatedTermsDrawerSection({
    source,
    entity,
    week,
    year,
    countries,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    name,
    relatedEntityName,
    filename = 'related keywords and hashtags',
    displayQty = 10,
    enabled = true,
    showIcon = false,
}) {
    const spec = BY_SOURCE[source];
    if (!spec) {
        throw new Error(
            `RelatedTermsDrawerSection: unsupported source "${source}"`,
        );
    }
    const csvColumns = [
        { key: 'term', label: 'term' },
        { key: 'type', label: 'type' },
        { key: 'volume', label: spec.volumeLabel },
    ];

    const sharedArgs = {
        entity,
        week,
        year,
        countries,
        categories,
        brands,
        relatedCategories,
        relatedBrands,
        per_page: 50,
        enabled,
    };
    const keywordsQuery = spec.useKeywords(sharedArgs);
    const hashtagsQuery = spec.useHashtags(sharedArgs);

    const isLoading = keywordsQuery.isLoading || hashtagsQuery.isLoading;

    const flagBase = {
        countries,
        categories,
        brands,
        dataSource: source,
        ...(entity === 'brand'
            ? { relatedBrandName: relatedEntityName }
            : { relatedCategoryName: relatedEntityName }),
    };

    const keywordTerms = (keywordsQuery.data?.data || []).map((row) => ({
        kw: row.keyword,
        term: row.keyword,
        type: 'keyword',
        volume: spec.volumeOf(row),
        id: row.id,
        flagId: row.id,
        flagData: getFlagData({
            ...flagBase,
            keywords: [row.id],
            keyword: row.keyword,
        }),
        fetching: isLoading,
        fetchingData: isLoading,
    }));
    const hashtagTerms = (hashtagsQuery.data?.data || []).map((row) => {
        const hashtagId = (spec.hashtagIdOf ?? hashtagIdOf)(row);
        return {
            kw: row.name,
            term: row.name,
            type: 'hashtag',
            volume: spec.volumeOf(row),
            id: hashtagId,
            flagId: row.id,
            flagData: getFlagData({
                ...flagBase,
                hashtags: [hashtagId],
                hashtag: row.name,
            }),
            fetching: isLoading,
            fetchingData: isLoading,
        };
    });

    const sortedData =
        keywordsQuery.data || hashtagsQuery.data
            ? [...keywordTerms, ...hashtagTerms].sort(
                  (a, b) => (b.volume ?? 0) - (a.volume ?? 0),
              )
            : null;

    const handleDownload = (phrases = sortedData) =>
        downloadItemsAsCSV(
            filename,
            (phrases || []).map(({ term, type, volume }) => ({
                term,
                type,
                volume,
            })),
            csvColumns,
        );

    const dataReady =
        sortedData && keywordsQuery.isSuccess && hashtagsQuery.isSuccess;

    return (
        <TopPhrasesSection
            data={{
                week,
                year,
                countries,
                categories,
                brands,
                dataSource: source,
            }}
            isLoading={isLoading}
            sortedData={sortedData}
            dataReady={dataReady}
            showTranslations={false}
            handleDownload={handleDownload}
            mixpanelMeta={{ contentKey: spec.sectionContentKey }}
            paginateBy="keywords"
            displayQty={displayQty}
            name={name}
            showIcon={showIcon}
            canFilterTermTypes
            WrapperComponent={Wrapper}
            HeadingComponent={TermsHeading}
            NoDataComponent={NoTerms}
            showMoreLabel="keywords & hashtags"
        />
    );
}

export default RelatedTermsDrawerSection;
